import { useEffect, useRef, useState, useCallback } from "react";
import { Send, Mic, Square, Volume2, VolumeX, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, streamChat } from "@/lib/api";
import { MessageBubble } from "./MessageBubble";
import { useVoiceRecorder, speak } from "./useVoice";

export default function ChatPanel({ scope = "main", color = "#00F0FF", conversationId, onConversation, prompts = [], compact = false }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceReply, setVoiceReply] = useState(false);
  const bottom = useRef(null);
  const abort = useRef(null);
  const voice = useVoiceRecorder(useCallback((t) => setInput((v) => (v ? v + " " + t : t)), []));

  useEffect(() => {
    if (!conversationId) { setMessages([]); return; }
    api.get(`/agent/conversations/${conversationId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, [conversationId]);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "", tools: [], streaming: true }]);
    const patch = (fn) => setMessages((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    abort.current = new AbortController();
    let final = "";
    try {
      await streamChat({ conversation_id: conversationId, scope, message: msg }, (ev) => {
        if (ev.type === "meta") { if (ev.conversation_id !== conversationId) onConversation?.(ev.conversation_id, ev.title); }
        else if (ev.type === "delta") { final += ev.content; patch((a) => ({ ...a, content: a.content + ev.content })); }
        else if (ev.type === "tool_start") patch((a) => ({ ...a, tools: [...(a?.tools || []), { name: ev.name, pending: true }] }));
        else if (ev.type === "tool_result") patch((a) => {
          const tools = [...(a?.tools || [])];
          const idx = tools.findIndex((t) => t.pending && t.name === ev.name);
          const done = { name: ev.name, args: ev.args, result: ev.result };
          idx >= 0 ? (tools[idx] = done) : tools.push(done);
          return { ...a, tools };
        });
        else if (ev.type === "error") toast.error(ev.message);
      }, abort.current.signal);
    } catch (e) { if (e.name !== "AbortError") toast.error(e.message); }
    patch((a) => ({ ...a, streaming: false }));
    setBusy(false);
    if (voiceReply && final) speak(final);
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid={`chat-panel-${scope}`}>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-6 space-y-5">
        {messages.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center fade-up">
            <div className="h-16 w-16 rounded-2xl grid place-items-center border mb-5 shadow-[0_0_60px_-10px_var(--glow)]" style={{ "--glow": color, color, background: `${color}14`, borderColor: `${color}44` }}>
              <Sparkles size={28} />
            </div>
            <h2 className={`${compact ? "text-lg" : "text-xl sm:text-2xl"} font-bold text-slate-50`}>{scope === "main" ? "أنا «عقل» — الوكيل السيادي للمنصة" : "الذكاء المدمج في هذا المصنع"}</h2>
            <p className="text-sm text-slate-400 mt-2 max-w-md">تكلّم بالعربية بالصوت أو الكتابة. أنفّذ أوامرك المركبة عبر المصانع وأعرض لك ما نفذته خطوة بخطوة.</p>
            {prompts.length > 0 && (
              <div className={`mt-8 grid gap-2 w-full ${compact ? "grid-cols-1" : "sm:grid-cols-2"} max-w-2xl`}>
                {prompts.map((p, i) => (
                  <button key={i} data-testid={`quick-prompt-card-${i}`} onClick={() => send(p)}
                    className="text-right rounded-xl border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 px-4 py-3 text-sm text-slate-200 transition-all hover:-translate-y-0.5">
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => <MessageBubble key={m.id || i} m={m} color={color} />)}
        <div ref={bottom} />
      </div>

      <div className="shrink-0 px-4 sm:px-6 pb-5 pt-2">
        <div className="glass rounded-2xl p-2 flex items-end gap-2 focus-within:ring-2 focus-within:ring-cyan-400/30 transition-shadow">
          <button data-testid="ai-voice-record-button" onClick={voice.toggle} disabled={busy || voice.transcribing} title="تسجيل صوتي"
            className={`h-11 w-11 shrink-0 rounded-xl grid place-items-center transition-colors ${voice.recording ? "bg-red-500 text-white pulse-ring" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            {voice.transcribing ? <Loader2 size={18} className="animate-spin" /> : voice.recording ? <Square size={16} /> : <Mic size={18} />}
          </button>
          <textarea data-testid="ai-chat-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={voice.recording ? "جارٍ الاستماع..." : "اكتب أمرك هنا… مثال: سجّل نطاق aql4.store واربطه بخادم جديد"}
            className="flex-1 resize-none bg-transparent px-2 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none max-h-40" />
          <button data-testid="ai-voice-reply-toggle" onClick={() => { setVoiceReply(!voiceReply); if (voiceReply) window.speechSynthesis?.cancel(); }} title="القراءة الصوتية للرد"
            className={`h-11 w-11 shrink-0 rounded-xl grid place-items-center transition-colors ${voiceReply ? "bg-cyan-400/15 text-cyan-200" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
            {voiceReply ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
          <button data-testid="ai-send-message-button" onClick={() => send()} disabled={busy || !input.trim()}
            className="h-11 w-11 shrink-0 rounded-xl grid place-items-center text-slate-950 disabled:opacity-40 transition-all hover:brightness-110 active:scale-95" style={{ background: color }}>
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} className="-scale-x-100" />}
          </button>
        </div>
      </div>
    </div>
  );
}
